import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { CashRegisterService } from 'src/app/services/cash-register/cash-register.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { lastValueFrom } from 'rxjs';
import Swal from 'sweetalert2';

/**
 * LA FICHA DE UNA TIENDA DE UN COMERCIO — página con ruta propia.
 *
 * 🔴 Era un modal dentro de la ficha del comercio, con cuatro datos. Dejó de
 * alcanzar el día que la tienda pasó a tener SUS CAJAS: un listado, un alta con
 * cuatro campos y una baja no caben en un modal, y encima el modal no tiene
 * dirección — no se puede compartir, no se puede volver con el botón de atrás y
 * recargar la página lo pierde.
 *
 * 📍 Vive bajo el comercio (`/console/merchants/:code/tiendas/:shopCode`) y no
 * bajo `/console/shops/:code`, porque se llega desde el comercio y la miga de
 * pan tiene que poder decir de dónde se viene. La tienda sola ya tiene su
 * pantalla vieja en el módulo de Tiendas; esta es la del comercio.
 *
 * ⚠️ NO trae un servicio nuevo: las cajas salen de `CashRegisterService`, que ya
 * existía en el repositorio con los endpoints reales, y la tienda sale de la
 * misma llamada que usa la ficha del comercio (`GET /api/merchant/:code`, que
 * devuelve el comercio con sus tiendas). Cero endpoints nuevos.
 */
@Component({
  selector: 'app-comercios-tienda',
  templateUrl: './comercios-tienda.component.html'
})
export class ComerciosTiendaComponent implements OnInit {

  codigo = '';        // el comercio
  codigoTienda = '';  // la tienda

  comercio: any = null;
  tienda: any = null;
  cargando = true;
  noEncontrada = false;

  cajas: any[] = [];
  cargandoCajas = false;
  errorCajas = '';

  /** El alta vive en la misma página. No hace falta otro modal. */
  formCaja: any = null;
  guardandoCaja = false;

  readonly TIPOS: any = { NORMAL: 'Normal', AUTOPAGO: 'Autopago' };

  constructor(
    private ruta: ActivatedRoute,
    private router: Router,
    private _merchant: MerchantService,
    private _cajas: CashRegisterService,
    public _auth: AuthService
  ) { }

  ngOnInit(): void {
    this.ruta.paramMap.subscribe(p => {
      this.codigo = p.get('code') || '';
      this.codigoTienda = p.get('shopCode') || '';
      this.cargar();
    });
  }

  /**
   * La tienda sale de la ficha del comercio: una sola llamada trae el nombre
   * del comercio —que hace falta para la miga de pan— y sus tiendas.
   */
  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.noEncontrada = false;
    try {
      const rep = await this._merchant.getMerchant(this.codigo);
      this.comercio = rep?.merchant || null;
      this.tienda = (rep?.shops || []).find((t: any) => t.code === this.codigoTienda) || null;
      this.noEncontrada = !this.tienda;
    } catch {
      this.comercio = null;
      this.tienda = null;
      this.noEncontrada = true;
    } finally {
      this.cargando = false;
    }
    if (this.tienda) this.cargarCajas();
  };

  cargarCajas = async (): Promise<void> => {
    this.cargandoCajas = true;
    this.errorCajas = '';
    try {
      const r: any = await lastValueFrom(this._cajas.getCashRegistersByShop(this.codigoTienda));
      this.cajas = r?.cashRegisters || r?.cash_registers || r?.data || [];
    } catch (e: any) {
      this.cajas = [];
      // ⚠️ Un error al traer las cajas NO puede verse igual que «no tiene
      // cajas»: uno es un problema del servidor y el otro es un dato.
      this.errorCajas = e?.error?.msg || 'No se pudieron traer las cajas de esta tienda.';
    } finally {
      this.cargandoCajas = false;
    }
  };

  volverATiendas = (): void => {
    // Vuelve a la pestaña de donde salió, no al principio de la ficha.
    // 🔴 A LA LISTA DE TIENDAS, no a la ficha con `?tab=tiendas`: desde el reparto
    // del 2026-09-20 ese parametro no lo lee nadie y caias en la ficha del comercio.
    this.router.navigate(['/console/merchants', this.codigo, 'tiendas']);
  };

  // ── el alta de una caja ───────────────────────────────────────────────────
  //
  // 🔴 Aquí se crea LA CAJA, que es una entidad de la tienda: número, afiliación
  // y tipo. No es el usuario que la opera — ese se crea en Usuarios del comercio
  // y se ASOCIA a una de estas. Lo que viaja estampado en la venta es esta caja,
  // no el nombre del usuario que la usó.

  abrirFormCaja = (): void => {
    // Propone el número que sigue: en una tienda con las cajas 1 y 2, propone 3.
    const usados = this.cajas.map(c => Number(c.cash_register_number) || 0);
    this.formCaja = {
      cash_register_number: (usados.length ? Math.max(...usados) : 0) + 1,
      afiliation_number: '',
      type: 'NORMAL',
      description: ''
    };
  };

  cerrarFormCaja = (): void => {
    if (this.guardandoCaja) return;
    this.formCaja = null;
  };

  get numeroCajaNuevo(): number | null {
    const n = Number(this.formCaja?.cash_register_number);
    return Number.isInteger(n) && n > 0 ? n : null;
  }

  /**
   * El índice de la base es único por `{tienda, número}`: dos cajas con el
   * mismo número no pueden existir. Se avisa antes de mandar, no después del
   * error del servidor.
   */
  get numeroRepetido(): boolean {
    const n = this.numeroCajaNuevo;
    return !!(n && this.cajas.some(c => Number(c.cash_register_number) === n));
  }

  get formCajaValido(): boolean {
    return !!(this.numeroCajaNuevo && !this.numeroRepetido &&
              String(this.formCaja?.afiliation_number || '').trim() &&
              this.formCaja?.type);
  }

  crearCaja = async (): Promise<void> => {
    if (!this.formCajaValido || this.guardandoCaja) return;
    this.guardandoCaja = true;
    try {
      await lastValueFrom(this._cajas.createCashRegister({
        shop_code: this.codigoTienda,
        cash_register_number: this.numeroCajaNuevo,          // 🔴 numérico, no texto
        afiliation_number: String(this.formCaja.afiliation_number).trim(),
        type: this.formCaja.type,
        ...(String(this.formCaja.description || '').trim()
              ? { description: String(this.formCaja.description).trim() } : {})
      }));
      this.guardandoCaja = false;
      this.formCaja = null;
      await this.cargarCajas();
      Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Caja creada',
                  timer: 1800, showConfirmButton: false });
    } catch (e: any) {
      // El formulario no se cierra: lo que se escribió se queda.
      Swal.fire({ icon: 'error', title: 'No se pudo crear la caja',
                  text: e?.error?.msg || e?.message || 'Intenta de nuevo.',
                  footer: e?.error?.detalle || e?.error?.errors?.[0]?.msg || '' });
    } finally {
      this.guardandoCaja = false;
    }
  };

  /**
   * ⚠️ Se DESACTIVA, no se borra. Una caja que ya cobró está estampada en
   * ventas: borrarla deja esas ventas apuntando a una caja que no existe.
   */
  desactivarCaja = async (c: any): Promise<void> => {
    const r = await Swal.fire({
      icon: 'warning',
      title: `¿Desactivar la caja ${c.cash_register_number}?`,
      text: 'Deja de poder cobrar. Las ventas que ya hizo se conservan.',
      showCancelButton: true, confirmButtonText: 'Desactivar', cancelButtonText: 'No'
    });
    if (!r.isConfirmed) return;
    try {
      await lastValueFrom(this._cajas.deleteCashRegister(c._id));
      await this.cargarCajas();
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'No se pudo desactivar la caja',
                  text: e?.error?.msg || 'Intenta de nuevo.' });
    }
  };

  tipoEnPalabras = (t: string): string => this.TIPOS[String(t || '').toUpperCase()] || '—';
}
